import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import {
  api,
  type ArekWeekDay,
  type ArekWeekFooter,
  type ArekWeekHeader,
  type ArekWeekItem,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { OpCallout } from "@/components/op/OpCallout";

const SECTION_ORDER = ["rank", "fokus", "utrzymanie", "nauka", "zamkniecie"] as const;
const SECTION_LABEL: Record<string, string> = {
  rank: "Rank",
  fokus: "Fokus",
  utrzymanie: "Utrzymanie",
  nauka: "Nauka",
  zamkniecie: "Zamknięcie",
};
const CATEGORY_OPTS = [
  { value: "biznes", label: "biznes" },
  { value: "ops", label: "ops" },
  { value: "nauka", label: "nauka" },
];
const PHASE_OPTS = [
  { value: "", label: "faza…" },
  { value: "sprzedaz", label: "sprzedaż" },
  { value: "przerwa", label: "przerwa" },
  { value: "zasieg", label: "zasięg" },
  { value: "edukacja", label: "edukacja" },
  { value: "warmup", label: "warm-up" },
];
const MODE_OPTS = [
  { value: "norma", label: "norma" },
  { value: "minimalna", label: "minimalna" },
];

function currentWeek() {
  const d = new Date();
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function newItemId() {
  return `item-${Math.random().toString(36).slice(2, 10)}`;
}

function groupBySection(items: ArekWeekItem[]) {
  const map: Record<string, ArekWeekItem[]> = {};
  for (const s of SECTION_ORDER) map[s] = [];
  for (const it of items) {
    const key = SECTION_ORDER.includes(it.section as (typeof SECTION_ORDER)[number])
      ? it.section
      : "utrzymanie";
    map[key].push(it);
  }
  return map;
}

function fieldClass() {
  return "w-full rounded-md border border-input bg-white px-3 py-2 text-sm";
}

export default function TydzienArka() {
  const qc = useQueryClient();
  const [week, setWeek] = useState(currentWeek);
  const [editTemplate, setEditTemplate] = useState(false);
  const [mergeOnSave, setMergeOnSave] = useState(true);

  const weekQ = useQuery({
    queryKey: ["arek-week", week],
    queryFn: () => api.arekWeek(week),
    enabled: !editTemplate,
  });
  const templateQ = useQuery({
    queryKey: ["arek-week-template"],
    queryFn: () => api.arekWeekTemplate(),
    enabled: editTemplate,
  });

  const [header, setHeader] = useState<ArekWeekHeader | null>(null);
  const [days, setDays] = useState<ArekWeekDay[] | null>(null);
  const [footer, setFooter] = useState<ArekWeekFooter | null>(null);
  const [tplDays, setTplDays] = useState<ArekWeekDay[] | null>(null);
  const [tplLimits, setTplLimits] = useState<string[] | null>(null);

  useEffect(() => {
    setHeader(null);
    setDays(null);
    setFooter(null);
  }, [week]);

  useEffect(() => {
    if (!editTemplate) {
      setTplDays(null);
      setTplLimits(null);
    }
  }, [editTemplate]);

  const shownHeader = header || weekQ.data?.header || {};
  const shownDays = days || weekQ.data?.days || [];
  const shownFooter = footer || weekQ.data?.footer || {};
  const shownTplDays = tplDays || templateQ.data?.days || [];
  const shownLimits = editTemplate
    ? tplLimits || templateQ.data?.limits || []
    : weekQ.data?.limits || [];

  const saveWeekM = useMutation({
    mutationFn: () =>
      api.arekWeekSave(week, {
        header: shownHeader,
        days: shownDays,
        footer: shownFooter,
      }),
    onSuccess: (res) => {
      setHeader(null);
      setDays(null);
      setFooter(null);
      qc.setQueryData(["arek-week", week], res);
    },
  });

  const checkM = useMutation({
    mutationFn: ({ item_id, done }: { item_id: string; done: boolean }) =>
      api.arekWeekCheck(week, item_id, done),
    onSuccess: (res) => {
      setDays(null);
      qc.setQueryData(["arek-week", week], res);
    },
  });

  const saveTplM = useMutation({
    mutationFn: () =>
      api.arekWeekTemplateSave({
        version: templateQ.data?.version || 1,
        days: shownTplDays,
        limits: shownLimits,
        merge_into_week: mergeOnSave ? week : undefined,
      }),
    onSuccess: (res) => {
      setTplDays(null);
      setTplLimits(null);
      qc.setQueryData(["arek-week-template"], res.template);
      if (res.week) qc.setQueryData(["arek-week", week], res.week);
      setEditTemplate(false);
    },
  });

  const resetTplM = useMutation({
    mutationFn: () => api.arekWeekTemplateReset(),
    onSuccess: (res) => {
      setTplDays(res.days);
      setTplLimits(res.limits || []);
      qc.setQueryData(["arek-week-template"], res);
    },
  });

  const progress = useMemo(() => {
    let total = 0;
    let done = 0;
    for (const d of shownDays) {
      for (const it of d.items || []) {
        total += 1;
        if (it.done) done += 1;
      }
    }
    return { total, done };
  }, [shownDays]);

  function patchHeader(fields: Partial<ArekWeekHeader>) {
    setHeader({ ...shownHeader, ...fields });
  }

  function patchFooter(fields: Partial<ArekWeekFooter>) {
    setFooter({ ...shownFooter, ...fields });
  }

  function toggleLocalThenCheck(item: ArekWeekItem, done: boolean) {
    setDays(
      shownDays.map((day) => ({
        ...day,
        items: (day.items || []).map((it) => (it.id === item.id ? { ...it, done } : it)),
      })),
    );
    checkM.mutate({ item_id: item.id, done });
  }

  function patchTplDay(weekday: number, patch: Partial<ArekWeekDay>) {
    setTplDays(
      shownTplDays.map((d) => (d.weekday === weekday ? { ...d, ...patch } : d)),
    );
  }

  function patchTplItem(weekday: number, itemId: string, patch: Partial<ArekWeekItem>) {
    setTplDays(
      shownTplDays.map((d) => {
        if (d.weekday !== weekday) return d;
        return {
          ...d,
          items: (d.items || []).map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
        };
      }),
    );
  }

  function addTplItem(weekday: number, section: string) {
    const item: ArekWeekItem = {
      id: newItemId(),
      text: "Nowa pozycja",
      section,
      category: section === "nauka" ? "nauka" : "ops",
    };
    setTplDays(
      shownTplDays.map((d) =>
        d.weekday === weekday ? { ...d, items: [...(d.items || []), item] } : d,
      ),
    );
  }

  function removeTplItem(weekday: number, itemId: string) {
    setTplDays(
      shownTplDays.map((d) =>
        d.weekday === weekday
          ? { ...d, items: (d.items || []).filter((it) => it.id !== itemId) }
          : d,
      ),
    );
  }

  const loading = editTemplate ? templateQ.isLoading : weekQ.isLoading;
  const error = editTemplate ? templateQ.isError : weekQ.isError;
  const gridDays = editTemplate ? shownTplDays : shownDays;

  return (
    <div className="-mx-2 rounded-2xl border border-border bg-[#f7f8fa] p-4 text-foreground shadow-sm sm:-mx-0 sm:p-6">
      <PageHeader
        title="Tydzień Arka"
        subtitle={
          editTemplate
            ? "Edycja master template. Zmiany obowiązują od nowych tygodni (albo merge do bieżącego)."
            : "Cały tydzień operatora: nauka, sprzedaż, ads, ops. Plan tygodnia = tylko treść MOFU."
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="w-28 bg-white"
              value={week}
              onChange={(e) => setWeek(e.target.value.toUpperCase())}
              aria-label="Tydzień ISO"
              disabled={editTemplate}
            />
            {editTemplate ? (
              <>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={mergeOnSave}
                    onChange={(e) => setMergeOnSave(e.target.checked)}
                  />
                  Merge do {week}
                </label>
                <Button variant="outline" onClick={() => resetTplM.mutate()} disabled={resetTplM.isPending}>
                  Przywróć domyślny
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setTplDays(null);
                    setTplLimits(null);
                    setEditTemplate(false);
                  }}
                >
                  Anuluj
                </Button>
                <Button variant="brand" onClick={() => saveTplM.mutate()} disabled={saveTplM.isPending}>
                  Zapisz template
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditTemplate(true)}>
                  Edytuj template
                </Button>
                <Button variant="brand" onClick={() => saveWeekM.mutate()} disabled={saveWeekM.isPending}>
                  Zapisz
                </Button>
              </>
            )}
          </div>
        }
      />
      <OpCallout variant="help" className="mb-4" title="Rola tego widoku">
        Tu jest tydzień operatora. Plan tygodnia (osobna strona) trzyma tylko treść MOFU.
      </OpCallout>

      {error ? (
        <p className="mb-3 text-sm text-destructive">Nie udało się wczytać danych.</p>
      ) : null}
      {saveWeekM.isError || saveTplM.isError || checkM.isError || resetTplM.isError ? (
        <p className="mb-3 text-sm text-destructive">Zapis nie przeszedł. Spróbuj ponownie.</p>
      ) : null}

      {!editTemplate ? (
        <div className="mb-4 grid gap-3 rounded-xl border border-border bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Faza cyklu</span>
            <Select
              value={shownHeader.cycle_phase || ""}
              onChange={(v) => patchHeader({ cycle_phase: v })}
              options={PHASE_OPTS}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Tryb</span>
            <Select
              value={shownHeader.mode || "norma"}
              onChange={(v) => patchHeader({ mode: v })}
              options={MODE_OPTS}
            />
          </label>
          <label className="space-y-1 text-xs sm:col-span-2">
            <span className="text-muted-foreground">Ból tygodnia</span>
            <Input
              className="bg-white"
              value={shownHeader.pain || ""}
              onChange={(e) => patchHeader({ pain: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Narzędzie z produktu</span>
            <Input
              className="bg-white"
              value={shownHeader.tool || ""}
              onChange={(e) => patchHeader({ tool: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">1 fokus Arka</span>
            <Input
              className="bg-white"
              value={shownHeader.focus || ""}
              onChange={(e) => patchHeader({ focus: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">1 eksperyment</span>
            <Input
              className="bg-white"
              value={shownHeader.experiment || ""}
              onChange={(e) => patchHeader({ experiment: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Pytanie nauki</span>
            <Input
              className="bg-white"
              value={shownHeader.learning_question || ""}
              onChange={(e) => patchHeader({ learning_question: e.target.value })}
            />
          </label>
          <div className="flex items-end text-xs text-muted-foreground sm:col-span-2 lg:col-span-4">
            Postęp: {progress.done}/{progress.total}
            {" · "}
            <Link className="underline" to="/plan-tygodnia">
              Plan tygodnia (treść)
            </Link>
          </div>
        </div>
      ) : null}

      {shownLimits.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
          {shownLimits.map((line) => (
            <span key={line} className="rounded-full border border-border bg-white px-2.5 py-1">
              {line}
            </span>
          ))}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Ładowanie…</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
          {gridDays.map((day) => {
            const bySection = groupBySection(day.items || []);
            return (
              <section
                key={day.weekday}
                className="flex min-h-[220px] flex-col rounded-xl border border-border bg-white p-3"
              >
                <header className="mb-2 border-b border-border/60 pb-2">
                  <div className="text-sm font-semibold">{day.label}</div>
                  {editTemplate ? (
                    <Input
                      className="mt-1 bg-white text-xs"
                      value={day.role || ""}
                      onChange={(e) => patchTplDay(day.weekday, { role: e.target.value })}
                    />
                  ) : (
                    <div className="text-[11px] text-muted-foreground">{day.role}</div>
                  )}
                </header>
                <div className="flex flex-1 flex-col gap-3">
                  {SECTION_ORDER.map((section) => {
                    const items = bySection[section] || [];
                    if (!editTemplate && items.length === 0) return null;
                    return (
                      <div key={section}>
                        <div className="mb-1 flex items-center justify-between gap-1">
                          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            {SECTION_LABEL[section] || section}
                          </span>
                          {editTemplate ? (
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground"
                              onClick={() => addTplItem(day.weekday, section)}
                              aria-label={`Dodaj do ${section}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                        </div>
                        <ul className="space-y-1.5">
                          {items.map((it) =>
                            editTemplate ? (
                              <li key={it.id} className="space-y-1 rounded-md border border-border/70 p-1.5">
                                <Input
                                  className="bg-white text-xs"
                                  value={it.text}
                                  onChange={(e) =>
                                    patchTplItem(day.weekday, it.id, { text: e.target.value })
                                  }
                                />
                                <div className="flex items-center gap-1">
                                  <Select
                                    className="flex-1"
                                    value={it.category || "ops"}
                                    onChange={(v) =>
                                      patchTplItem(day.weekday, it.id, { category: v })
                                    }
                                    options={CATEGORY_OPTS}
                                  />
                                  <button
                                    type="button"
                                    className="rounded p-1 text-muted-foreground hover:text-destructive"
                                    onClick={() => removeTplItem(day.weekday, it.id)}
                                    aria-label="Usuń"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </li>
                            ) : (
                              <li key={it.id}>
                                <label
                                  className={cn(
                                    "flex cursor-pointer items-start gap-2 rounded-md px-1 py-0.5 text-xs leading-snug",
                                    it.done && "text-muted-foreground line-through",
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={!!it.done}
                                    onChange={(e) => toggleLocalThenCheck(it, e.target.checked)}
                                  />
                                  <span>{it.text}</span>
                                </label>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {!editTemplate ? (
        <div className="mt-4 grid gap-3 rounded-xl border border-border bg-white p-4 sm:grid-cols-2">
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Wynik tygodnia</span>
            <textarea
              className={fieldClass()}
              rows={2}
              value={shownFooter.result || ""}
              onChange={(e) => patchFooter({ result: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Czerwone pole scorecardu</span>
            <textarea
              className={fieldClass()}
              rows={2}
              value={shownFooter.red_metric || ""}
              onChange={(e) => patchFooter({ red_metric: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Decyzja na kolejny tydzień</span>
            <textarea
              className={fieldClass()}
              rows={2}
              value={shownFooter.decision || ""}
              onChange={(e) => patchFooter({ decision: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Parking</span>
            <textarea
              className={fieldClass()}
              rows={2}
              value={shownFooter.parking || ""}
              onChange={(e) => patchFooter({ parking: e.target.value })}
            />
          </label>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-border bg-white p-4">
          <div className="mb-2 text-xs font-medium text-muted-foreground">Limity (jedna linia = jeden chip)</div>
          <textarea
            className={fieldClass()}
            rows={4}
            value={(tplLimits || shownLimits).join("\n")}
            onChange={(e) =>
              setTplLimits(
                e.target.value
                  .split("\n")
                  .map((l) => l.trim())
                  .filter(Boolean),
              )
            }
          />
        </div>
      )}
    </div>
  );
}
